import { onMounted, onBeforeUnmount } from 'vue';

export interface UseKeyboardCloseOptions {
  onEscape: (event: KeyboardEvent) => void;
  container?: () => HTMLElement | null;
  topmostSelector?: string;
  requireFocusInside?: boolean;
}

const OPEN_POPUP = '.dropdown-menu.show, [aria-haspopup][aria-expanded="true"]';

const isEscape = (event: KeyboardEvent) =>
  event.key === 'Escape' || event.key === 'Esc' || event.which === 27;

export function useKeyboardClose(options: UseKeyboardCloseOptions) {
  const {
    onEscape,
    container,
    topmostSelector = '.modal.show',
    requireFocusInside = false,
  } = options;

  const claimForOpenPopup = (event: KeyboardEvent) => {
    const target = event.target as Element | null;
    if (isEscape(event) && target?.closest?.(OPEN_POPUP)) {
      event.preventDefault();
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isEscape(event) || event.defaultPrevented || event.repeat) {
      return;
    }

    const modals = document.querySelectorAll<HTMLElement>(topmostSelector);
    const top = modals[modals.length - 1] || null;

    if (container) {
      const el = container();
      if (!el || top !== el) {
        return;
      }
      if (requireFocusInside) {
        const active = document.activeElement as Node | null;
        if (!active || !el.contains(active)) {
          return;
        }
      }
    }

    event.preventDefault();
    onEscape(event);
  };

  onMounted(() => {
    window.addEventListener('keydown', claimForOpenPopup, true);
    window.addEventListener('keydown', handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', claimForOpenPopup, true);
    window.removeEventListener('keydown', handleKeyDown);
  });

  return { handleKeyDown };
}
